import React, { useEffect, useMemo, useState } from "react";
import { Animated } from "react-animated-css";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import ModalLoading from "../../workqueue/Modal/ModalLoading";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls/Card";

function DispatchList() {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("LastSentDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchDispatch = (search = searchTerm) => {
    setLoadError(null);
    setIsLoading(true);
    const qry =
      `?search=${encodeURIComponent(search || "")}` +
      `&sortColumn=${encodeURIComponent(sortColumn)}` +
      `&sortDirection=${encodeURIComponent(sortDirection)}` +
      `&pageIndex=${currentPage}` +
      `&pageSize=${rowsPerPage}`;

    Api.getRequestQry(Api.Uri_CmnDispatchList, qry)
      .then((res) => {
        if (res?.data?.statusCode === 200 && res?.data?.result) {
          setTotalRows(res.data.result.totalRows || 0);
          setItems(res.data.result.items || []);
          return;
        }
        setItems([]);
        setTotalRows(0);
      })
      .catch((ex) => {
        Common.LogError(ex, "DispatchList", "fetchDispatch");
        setItems([]);
        setTotalRows(0);
        setLoadError("Unable to load dispatch packets. Please try again.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, sortColumn, sortDirection]);

  const handleFilter = useMemo(
    () =>
      debounce((val) => {
        setCurrentPage(1);
        fetchDispatch(val);
      }, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsPerPage, sortColumn, sortDirection]
  );

  const handleSort = (column, direction) => {
    setSortColumn(column.sortColumn || "LastSentDate");
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handlePagination = (page) => setCurrentPage(page.selected + 1);

  const handlePerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    if (isNaN(d)) return "--";
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const renderStatusBadge = (status) => {
    const s = (status || "").toString().trim().toLowerCase();
    const cls =
      s === "sent"
        ? "badge badge-info"
        : s === "signed"
          ? "badge badge-success"
          : s === "failed"
            ? "badge badge-danger"
            : "badge badge-secondary";
    return <span className={cls}>{status || "--"}</span>;
  };

  const columns = [
    {
      name: "Packet ID",
      sortable: true,
      sortColumn: "PacketId",
      minWidth: "140px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.packetId, "--"),
    },
    {
      name: "Current Status",
      sortable: true,
      sortColumn: "CurrentStatus",
      minWidth: "140px",
      selector: (row) => row.currentStatus,
      cell: (row) => renderStatusBadge(row.currentStatus),
    },
    {
      name: "Provider Name",
      sortable: true,
      sortColumn: "ProviderName",
      minWidth: "160px",
      selector: (row) => row.providerName,
      cell: (row) =>
        row.providerName ? (
          <button type="button" className="btn btn-link p-0 text-left">
            {row.providerName}
          </button>
        ) : (
          "--"
        ),
    },
    {
      name: "Facility Name",
      sortable: true,
      sortColumn: "FacilityName",
      minWidth: "180px",
      selector: (row) => row.facilityName,
      cell: (row) =>
        row.facilityName ? (
          <button type="button" className="btn btn-link p-0 text-left">
            {row.facilityName}
          </button>
        ) : (
          "--"
        ),
    },
    {
      name: "Delivery Method",
      sortable: true,
      sortColumn: "DeliveryMethod",
      minWidth: "150px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.deliveryMethod, "--"),
    },
    {
      name: "Date First Sent",
      sortable: true,
      sortColumn: "DateFirstSent",
      minWidth: "140px",
      selector: (row) => row.dateFirstSent,
      cell: (row) => formatDate(row.dateFirstSent),
    },
    {
      name: "Last Sent Date",
      sortable: true,
      sortColumn: "LastSentDate",
      minWidth: "140px",
      selector: (row) => row.lastSentDate,
      cell: (row) => formatDate(row.lastSentDate),
    },
    {
      name: "Number of Resends",
      sortable: true,
      sortColumn: "ResendCount",
      minWidth: "170px",
      selector: (row) => row.resendCount,
      cell: (row) => (row.resendCount != null ? row.resendCount : 0),
    },
    {
      name: "Days Pending",
      sortable: true,
      sortColumn: "DaysPending",
      minWidth: "130px",
      selector: (row) => row.daysPending,
      cell: (row) => (row.daysPending != null ? row.daysPending : "--"),
    },
    {
      name: "Signed Date",
      sortable: true,
      sortColumn: "SignedDate",
      minWidth: "140px",
      selector: (row) => row.signedDate,
      cell: (row) => formatDate(row.signedDate),
    },
  ];

  const CustomPagination = () => {
    const count = Math.ceil(totalRows / rowsPerPage) || 1;
    return (
      <ReactPaginate
        previousLabel=""
        nextLabel=""
        pageCount={count}
        activeClassName="active"
        forcePage={currentPage !== 0 ? currentPage - 1 : 0}
        onPageChange={handlePagination}
        pageClassName="page-item"
        nextLinkClassName="page-link"
        nextClassName="page-item next"
        previousClassName="page-item prev"
        previousLinkClassName="page-link"
        pageLinkClassName="page-link"
        containerClassName="pagination react-paginate justify-content-end my-2 pe-1"
      />
    );
  };

  return (
    <Animated animationIn="fadeIn" animationInDuration={500}>
      <Card className="example example-compact">
        <CardHeader title="Dispatch" />
        <CardBody>
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: "1rem" }}>
            <div>
              <div className="font-weight-bold" style={{ fontSize: "1.05rem" }}>
                All CMNs
              </div>
            </div>
          </div>

          {loadError && (
            <div className="alert alert-warning" role="alert">
              <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "1rem" }}>
                <div>{loadError}</div>
                <button type="button" className="btn btn-sm btn-light" onClick={() => fetchDispatch()}>
                  Retry
                </button>
              </div>
            </div>
          )}

          <div className="react-dataTable">
            <DataTable
              noHeader
              striped
              highlightOnHover
              responsive
              pagination
              paginationServer
              sortServer
              persistTableHead
              subHeader
              columns={columns}
              data={items}
              onSort={handleSort}
              progressPending={isLoading}
              progressComponent={<ModalLoading />}
              paginationComponent={CustomPagination}
              className="react-dataTable"
              noDataComponent={
                <div className="w-100">
                  <div className="d-flex justify-content-center">
                    <div className="text-center py-5 text-muted" style={{ maxWidth: "720px" }}>
                      <div className="mb-2 font-weight-bold">No dispatch packets available yet.</div>
                      <div style={{ fontSize: "0.9rem" }}>Try broadening search filters or page range.</div>
                    </div>
                  </div>
                </div>
              }
              subHeaderComponent={
                <div className="w-100 mb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "1rem" }}>
                    <div className="d-flex align-items-center">
                      <input
                        id="dispatch-search"
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        placeholder="Search by order id, number"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          handleFilter(e.target.value);
                        }}
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      <label className="mb-0 mr-2" htmlFor="dispatch-per-page">
                        Show
                      </label>
                      <select
                        id="dispatch-per-page"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={handlePerPage}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ml-2 text-muted">entries</span>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
        </CardBody>
      </Card>
    </Animated>
  );
}

export default DispatchList;
